import { Box } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { TableTitleBox } from '../../common/components';
import { useGerencialesStatistics } from './hooks/useGerencialesStatistics';
import {
  GERENCIALES_REPORT_OPTIONS,
  GERENCIALES_REPORT_TYPE,
  GERENCIALES_PAYMENTS_REPORT_TYPE,
  GERENCIALES_DEBT_BALANCE_REPORT_TYPE,
  GERENCIALES_DEBT_STATUS_REPORT_TYPE,
  GERENCIALES_DEBT_STATUS_VIEW_TABLE,
  GERENCIALES_DEBT_STATUS_VIEW_CHART,
  GERENCIALES_PERSONAL_DATA_REPORT_TYPE,
  GERENCIALES_APP_SUBSCRIBERS_REPORT_TYPE,
  GERENCIALES_ARCHIVED_CONTRACTS_REPORT_TYPE,
  GERENCIALES_PDF_REPORTS,
} from './constants/gerencialesConstants';

import GerencialesFilters from './components/GerencialesFilters';
import ContractDetailsReport from './components/contract-details/ContractDetailsReport';
import PaymentsReport from './components/payments-report/PaymentsReport';
import DebtBalanceReport from './components/debt-balance-report/DebtBalanceReport';
import DebtStatusReport from './components/debt-status-report/DebtStatusReport';
import PersonalDataReport from './components/personal-data-report/PersonalDataReport';
import AppSubscribersReport from './components/app-subscribers-report/AppSubscribersReport';
import ArchivedContractsReport from './components/archived-contracts-report/ArchivedContractsReport';
import {
  generateContractDetailsPdf,
  generatePaymentsReportPdf,
  generateDebtStatusChartsPdf,
  generateAppSubscribersChartsPdf,
  generateArchivedContractsChartsPdf,
} from './utils/gerencialesPdfUtils';
import { useExcelExport } from '../habitat-99/hooks/useExcelExport';

const GerencialesStatistics = () => {
  const {
    loading,
    error,
    results,
    header,
    reportType,
    filters,
    reload,
    debtStatusFilters,
    debtStatusPagination,
    appSubscribersFilters,
    appSubscribersPagination,
    archivedContractsFilters,
    archivedContractsPagination,
    paymentMethods,
    paymentMethodsLoading,
    releaseReasons,
    releaseReasonsLoading,
    handleReportTypeChange,
    handleDateChange,
    handleDebtStatusFilterChange,
    handleDebtStatusPageChange,
    handleDebtStatusRowsPerPageChange,
    handleAppSubscribersFilterChange,
    handleAppSubscribersPageChange,
    handleAppSubscribersRowsPerPageChange,
    handleArchivedContractsFilterChange,
    handleArchivedContractsPageChange,
    handleArchivedContractsRowsPerPageChange,
    fetchDebtStatusExport,
    fetchAppSubscribersExport,
    fetchArchivedContractsExport,
  } = useGerencialesStatistics();
  const [isDownloading, setIsDownloading] = useState(false);
  const [isPdfCooldown, setIsPdfCooldown] = useState(false);
  const [isDownloadingXlsx, setIsDownloadingXlsx] = useState(false);
  const [reportView, setReportView] = useState(GERENCIALES_DEBT_STATUS_VIEW_TABLE);
  const pdfCooldownTimeoutRef = useRef(null);
  const { exportToExcel } = useExcelExport();

  /**
   * Valida si hay datos para exportar.
   * @param {Object|null} reportResults - Resultados del reporte.
   * @returns {boolean} Indica si hay data disponible.
   */
  const hasReportData = (reportResults) => {
    if (!reportResults) return false;
    if (Array.isArray(reportResults)) return reportResults.length > 0;
    return Object.values(reportResults).some(
      (value) => Array.isArray(value) && value.length > 0
    );
  };

  /**
   * Maneja la descarga del PDF según el tipo de reporte seleccionado.
   */
  const handleDownloadPdf = async () => {
    if (!hasReportData(results)) return;

    setIsDownloading(true);
    try {
      // Generar PDF según el tipo de reporte
      switch (reportType) {
        case GERENCIALES_REPORT_TYPE:
          await generateContractDetailsPdf(results, header);
          break;
        case GERENCIALES_PAYMENTS_REPORT_TYPE:
          await generatePaymentsReportPdf(results, header);
          break;
        case GERENCIALES_DEBT_STATUS_REPORT_TYPE:
          if (reportView === GERENCIALES_DEBT_STATUS_VIEW_CHART) {
            await generateDebtStatusChartsPdf(results, header);
          }
          break;
        case GERENCIALES_APP_SUBSCRIBERS_REPORT_TYPE:
          if (reportView === GERENCIALES_DEBT_STATUS_VIEW_CHART) {
            await generateAppSubscribersChartsPdf(results, header);
          }
          break;
        case GERENCIALES_ARCHIVED_CONTRACTS_REPORT_TYPE:
          if (reportView === GERENCIALES_DEBT_STATUS_VIEW_CHART) {
            await generateArchivedContractsChartsPdf(results, header);
          }
          break;
        // Aquí se pueden agregar más casos para otros tipos de reportes
        default:
          console.warn(`Tipo de reporte no soportado: ${reportType}`);
      }
    } catch (error) {
      console.error('Error al generar el PDF:', error);
    } finally {
      setIsDownloading(false);
    }
  };

  /**
   * Maneja la descarga del XLSX para el reporte de mora por estado.
   */
  const handleDownloadXlsx = async () => {
    if (
      reportType !== GERENCIALES_DEBT_STATUS_REPORT_TYPE &&
      reportType !== GERENCIALES_APP_SUBSCRIBERS_REPORT_TYPE &&
      reportType !== GERENCIALES_ARCHIVED_CONTRACTS_REPORT_TYPE
    ) {
      return;
    }
    if (reportView !== GERENCIALES_DEBT_STATUS_VIEW_TABLE) return;

    setIsDownloadingXlsx(true);
    try {
      const exportRows =
        reportType === GERENCIALES_DEBT_STATUS_REPORT_TYPE
          ? await fetchDebtStatusExport()
          : reportType === GERENCIALES_APP_SUBSCRIBERS_REPORT_TYPE
            ? await fetchAppSubscribersExport()
            : await fetchArchivedContractsExport();
      if (!exportRows.length) {
        return;
      }

      if (reportType === GERENCIALES_DEBT_STATUS_REPORT_TYPE) {
        const excelRows = exportRows.map((row) => ({
          Num_orden: row.contract_number,
          Suscriptor: Array.isArray(row.subscriber_names)
            ? row.subscriber_names.join(' / ')
            : 'Sin suscriptor',
          Poseedor: row.is_possessor ? 'SI' : 'NO',
          'Forma pago': row.payment_method,
          'Cupones en mora': row.overdue_installments,
          'Importe mora': Number(row.debt_amount ?? 0),
          Estado: row.debt_status,
        }));

        exportToExcel(excelRows, 'Informe_Mora_Por_Estado', 'Mora');
      } else {
        const excelRows = exportRows.map((row) => ({
          ...(reportType === GERENCIALES_APP_SUBSCRIBERS_REPORT_TYPE
            ? {
                Suscriptor: row.subscriber_name || 'Sin suscriptor',
                Email: row.email || '-',
                Celular: row.phone || '-',
                'Num. contrato': row.contract_number,
                'Usa app': row.uses_app ? 'SI' : 'NO',
              }
            : {
                'Num. contrato': row.contract_number,
                Suscriptores: row.subscriber_names || 'Sin suscriptor',
                'Motivo baja': row.release_reason || 'Sin motivo',
                'Fecha baja': row.deleted_at || '',
                'Contacto email': row.contact_email || '-',
                'Contacto telefono': row.contact_phone || '-',
              }),
        }));

        exportToExcel(
          excelRows,
          reportType === GERENCIALES_APP_SUBSCRIBERS_REPORT_TYPE
            ? 'Suscriptores_App_Celular'
            : 'Motivos_Baja_Contratos',
          reportType === GERENCIALES_APP_SUBSCRIBERS_REPORT_TYPE
            ? 'Suscriptores'
            : 'Bajas'
        );
      }
    } catch (error) {
      console.error('Error al generar el XLSX:', error);
    } finally {
      setIsDownloadingXlsx(false);
    }
  };

  useEffect(() => {
    if (
      reportType !== GERENCIALES_DEBT_STATUS_REPORT_TYPE &&
      reportType !== GERENCIALES_APP_SUBSCRIBERS_REPORT_TYPE &&
      reportType !== GERENCIALES_ARCHIVED_CONTRACTS_REPORT_TYPE
    ) {
      setReportView(GERENCIALES_DEBT_STATUS_VIEW_TABLE);
    }
  }, [reportType]);

  const handleReportViewChange = (event) => {
    setReportView(event.target.value);
  };

  useEffect(() => {
    if (pdfCooldownTimeoutRef.current) {
      clearTimeout(pdfCooldownTimeoutRef.current);
      pdfCooldownTimeoutRef.current = null;
    }

    if (!GERENCIALES_PDF_REPORTS.includes(reportType)) {
      setIsPdfCooldown(false);
      return;
    }

    const requiresChartView =
      reportType === GERENCIALES_DEBT_STATUS_REPORT_TYPE ||
      reportType === GERENCIALES_APP_SUBSCRIBERS_REPORT_TYPE ||
      reportType === GERENCIALES_ARCHIVED_CONTRACTS_REPORT_TYPE;

    if (requiresChartView && reportView !== GERENCIALES_DEBT_STATUS_VIEW_CHART) {
      setIsPdfCooldown(false);
      return;
    }

    if (loading || !hasReportData(results)) {
      setIsPdfCooldown(false);
      return;
    }

    setIsPdfCooldown(true);
    pdfCooldownTimeoutRef.current = setTimeout(() => {
      setIsPdfCooldown(false);
    }, 2500);

    return () => {
      if (pdfCooldownTimeoutRef.current) {
        clearTimeout(pdfCooldownTimeoutRef.current);
      }
    };
  }, [loading, reportType, reportView, results]);

  const canDownloadPdf =
    GERENCIALES_PDF_REPORTS.includes(reportType) &&
    (reportType !== GERENCIALES_DEBT_STATUS_REPORT_TYPE &&
      reportType !== GERENCIALES_APP_SUBSCRIBERS_REPORT_TYPE &&
      reportType !== GERENCIALES_ARCHIVED_CONTRACTS_REPORT_TYPE
      ? true
      : reportView === GERENCIALES_DEBT_STATUS_VIEW_CHART);

  return (
    <Box>
      <TableTitleBox title="Estadisticas | Gerenciales" />
      <GerencialesFilters
        reportType={reportType}
        reportOptions={GERENCIALES_REPORT_OPTIONS}
        loading={loading}
        error={error}
        header={header}
        dateStart={filters.dateStart}
        dateEnd={filters.dateEnd}
        onDateChange={handleDateChange}
        onReportTypeChange={handleReportTypeChange}
        onReload={reload}
        onDownloadPdf={handleDownloadPdf}
        pdfCooldown={isPdfCooldown}
        disableDownload={
          !canDownloadPdf ||
          !hasReportData(results) ||
          isDownloading ||
          isPdfCooldown
        }
        onDownloadXlsx={handleDownloadXlsx}
        disableDownloadXlsx={
          (reportType !== GERENCIALES_DEBT_STATUS_REPORT_TYPE &&
            reportType !== GERENCIALES_APP_SUBSCRIBERS_REPORT_TYPE &&
            reportType !== GERENCIALES_ARCHIVED_CONTRACTS_REPORT_TYPE) ||
          reportView !== GERENCIALES_DEBT_STATUS_VIEW_TABLE ||
          !hasReportData(results) ||
          isDownloadingXlsx
        }
        showDownloadXlsx={
          (reportType === GERENCIALES_DEBT_STATUS_REPORT_TYPE ||
            reportType === GERENCIALES_APP_SUBSCRIBERS_REPORT_TYPE ||
            reportType === GERENCIALES_ARCHIVED_CONTRACTS_REPORT_TYPE) &&
          reportView === GERENCIALES_DEBT_STATUS_VIEW_TABLE
        }
        debtStatusFilters={debtStatusFilters}
        onDebtStatusFilterChange={handleDebtStatusFilterChange}
        appSubscribersFilters={appSubscribersFilters}
        onAppSubscribersFilterChange={handleAppSubscribersFilterChange}
        archivedContractsFilters={archivedContractsFilters}
        onArchivedContractsFilterChange={handleArchivedContractsFilterChange}
        reportView={reportView}
        onReportViewChange={handleReportViewChange}
        paymentMethods={paymentMethods}
        paymentMethodsLoading={paymentMethodsLoading}
        releaseReasons={releaseReasons}
        releaseReasonsLoading={releaseReasonsLoading}
      />
      {reportType === GERENCIALES_REPORT_TYPE && (
        <ContractDetailsReport loading={loading} results={results} header={header} />
      )}
      {reportType === GERENCIALES_PAYMENTS_REPORT_TYPE && (
        <PaymentsReport loading={loading} results={results} header={header} />
      )}
      {reportType === GERENCIALES_DEBT_BALANCE_REPORT_TYPE && (
        <DebtBalanceReport loading={loading} results={results} header={header} />
      )}
      {reportType === GERENCIALES_DEBT_STATUS_REPORT_TYPE && (
        <DebtStatusReport
          loading={loading}
          results={results}
          header={header}
          reportView={reportView}
          onReload={reload}
          pagination={debtStatusPagination}
          onPageChange={handleDebtStatusPageChange}
          onRowsPerPageChange={handleDebtStatusRowsPerPageChange}
        />
      )}
      {reportType === GERENCIALES_PERSONAL_DATA_REPORT_TYPE && (
        <PersonalDataReport loading={loading} results={results} header={header} />
      )}
      {reportType === GERENCIALES_APP_SUBSCRIBERS_REPORT_TYPE && (
        <AppSubscribersReport
          loading={loading}
          results={results}
          header={header}
          reportView={reportView}
          pagination={appSubscribersPagination}
          onPageChange={handleAppSubscribersPageChange}
          onRowsPerPageChange={handleAppSubscribersRowsPerPageChange}
        />
      )}
      {reportType === GERENCIALES_ARCHIVED_CONTRACTS_REPORT_TYPE && (
        <ArchivedContractsReport
          loading={loading}
          results={results}
          header={header}
          reportView={reportView}
          pagination={archivedContractsPagination}
          onPageChange={handleArchivedContractsPageChange}
          onRowsPerPageChange={handleArchivedContractsRowsPerPageChange}
        />
      )}
    </Box>
  );
};

export default GerencialesStatistics;
